"use client";

import { BackgroundOrbits } from "@/components/BackgroundOrbits";
import Blog from "@/components/index/fragments/Blog";
import WhatIsWatchDog from "@/components/index/fragments/WhatIsIt";
import About from "@/components/index/sections/About";
import About3 from "@/components/index/sections/About3";
import Demo from "@/components/index/sections/Demo";
import Hero from "@/components/index/sections/Hero";
import PriceList from "@/components/index/sections/PriceList";
import Reviews from "@/components/index/sections/Reviews";
import SectionFrame from "@/components/index/sections/SectionFrame";
import { Button } from "@/components/ui/Button";
import { Panel } from "@/components/ui/Panel";
import { getSiteContent } from "@/content/site";
import { useAppContext } from "@/context/AppContext";
import { useLanguage } from "@/context/LanguageContext";
import { HOME_FAQ } from "@/lib/seo";
import Link from "next/link";
import React from "react";

type LandingSection = {
  id?: string;
  className: string;
  label?: string;
  title?: string;
  subtitle?: string;
  content: React.ReactNode;
};

function getLandingSections(language: "cs" | "en"): LandingSection[] {
  const copy = {
    cs: {
      pricingLabel: "Členství",
      pricingTitle: "Vyberte si plán, který vám sedí",
      pricingSubtitle: "Začněte zdarma - bez kreditní karty.",
      demoLabel: "Ukázka",
      demoTitle: "Podívejte se, jak to funguje",
      demoSubtitle:
        "Přehledné reporty přímo v mailu nebo na webu - vždy víte, co se děje.",
    },
    en: {
      pricingLabel: "Membership",
      pricingTitle: "Choose the plan that fits you",
      pricingSubtitle: "Start free - no credit card, no commitment.",
      demoLabel: "Interactive demo",
      demoTitle: "See how it works",
      demoSubtitle:
        "Clear reports by email or on the web - you always know what is happening.",
    },
  }[language];

  return [
    {
      id: "o-psovi",
      className: "section-watchdog",
      content: <WhatIsWatchDog />,
    },
    {
      id: "blog",
      className: "section-blog",
      content: <Blog limit={4} />,
    },
    {
      id: "cenik",
      className: "section-pricing",
      label: copy.pricingLabel,
      title: copy.pricingTitle,
      subtitle: copy.pricingSubtitle,
      content: <PriceList />,
    },
    {
      id: "demo",
      className: "section-demo",
      label: copy.demoLabel,
      title: copy.demoTitle,
      subtitle: copy.demoSubtitle,
      content: <Demo />,
    },
    {
      id: "jak-to-funguje",
      className: "section-how",
      content: <About />,
    },
  ];
}

function TrustStrip() {
  const { language } = useLanguage();
  const { trustStats } = getSiteContent(language);

  return (
    <section className="trust-strip">
      <div className="trust-strip-inner">
        {trustStats.map((stat) => (
          <div key={stat.label} className="trust-stat">
            <span className="trust-stat-value">{stat.value}</span>
            <span className="trust-stat-label">{stat.label}</span>
          </div>
        ))}
      </div>
    </section>
  );
}

function LandingCta() {
  const { language } = useLanguage();
  const copy = {
    cs: {
      title: "Začněte sledovat nemovitosti ještě dnes",
      subtitle: "Prvních 14 dní zdarma. Žádná kreditní karta.",
      action: "Vytvořit hlídacího psa ->",
    },
    en: {
      title: "Start tracking properties today",
      subtitle: "First 14 days free. No credit card.",
      action: "Create a property watchdog ->",
    },
  }[language];

  return (
    <section className="section-cta-banner">
      <div className="cta-banner-inner">
        <h2 className="section-title">{copy.title}</h2>
        <p className="cta-banner-sub">{copy.subtitle}</p>
        <Button asChild variant="default">
          <Link href="/#cenik">{copy.action}</Link>
        </Button>
      </div>
    </section>
  );
}

function SeoInformationSections() {
  return (
    <>
      <section id="jak-funguje-hlidaci-pes" className="section-seo">
        <div className="mx-auto max-w-6xl">
          <div className="blog-header">
            <span className="section-label">Hlídací pes na nemovitosti</span>
            <h2 className="section-title text-softiq-copy dark:text-softiq-copy-inverse">
              Jak SOFTIQ sleduje nové realitní nabídky
            </h2>
            <p className="section-subtitle">
              Nastavíte, co hledáte, a služba za vás průběžně kontroluje
              důležité české realitní portály.
            </p>
          </div>
          <div className="grid gap-5 md:grid-cols-3">
            {[
              [
                "1. Zvolíte kritéria",
                "Vyberete lokalitu, cenu, typ nabídky, druh nemovitosti a další dostupné parametry.",
              ],
              [
                "2. Hlídací pes hledá",
                "SOFTIQ průběžně porovnává nové a aktualizované inzeráty s vaším nastavením.",
              ],
              [
                "3. Dostanete přehled",
                "Výsledky uvidíte na webu a podle členství je dostanete také e-mailem.",
              ],
            ].map(([title, text]) => (
              <Panel as="article" key={title} className="p-7">
                <h3 className="app-content-title">{title}</h3>
                <p className="app-content-description mt-3">{text}</p>
              </Panel>
            ))}
          </div>
        </div>
      </section>

      <section id="caste-dotazy" className="section-faq">
        <div className="mx-auto max-w-4xl">
          <div className="blog-header">
            <span className="section-label">Časté dotazy</span>
            <h2 className="section-title text-softiq-copy dark:text-softiq-copy-inverse">
              Co vědět o správě nemovitostí v SOFTIQ
            </h2>
          </div>
          <div className="space-y-4">
            {HOME_FAQ.map((item) => (
              <Panel as="details" key={item.question} className="p-6">
                <summary className="cursor-pointer text-lg font-semibold text-softiq-copy dark:text-softiq-copy-inverse">
                  {item.question}
                </summary>
                <p className="mt-4 leading-7 text-foreground/80">
                  {item.answer}
                </p>
              </Panel>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}

function PublicLandingSections() {
  const { language } = useLanguage();
  const landingSections = getLandingSections(language);

  return (
    <>
      <TrustStrip />
      {landingSections.map((section, index) => (
        <SectionFrame
          key={`${section.className}-${index}`}
          id={section.id}
          className={section.className}
          label={section.label}
          title={section.title}
          subtitle={section.subtitle}
        >
          {section.content}
        </SectionFrame>
      ))}
      <SectionFrame className="section-about3">
        <div className="dot-bg-dark" aria-hidden />
        <About3 />
      </SectionFrame>
      <SectionFrame id="recenze" className="section-reviews">
        <Reviews />
      </SectionFrame>
      <SeoInformationSections />
      <LandingCta />
    </>
  );
}

export function HomeLandingContent() {
  const { isLoggedIn } = useAppContext();

  return (
    <div className="page-root">
      <BackgroundOrbits />
      <Hero />
      {isLoggedIn ? <Reviews dialogOnly /> : <PublicLandingSections />}
    </div>
  );
}
